import 'css-skeletons/dist/css-skeletons.min.css';

import classNames from 'classnames';
import type {
  LngLatBoundsLike,
  Map as MapLibreMap,
  MapOptions,
} from 'maplibre-gl';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useChartTheme } from '@revelio/replots';
import { Text } from '@revelio/ui';

import { useKibanaLogger } from '../../../../../shared/hooks/kibana-logger/kibana-logger';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useFilters } from '../../../filters/hooks/useFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import { PageType } from '../../PageConfigProvider/types';
import type {
  DataNormalization,
  DataTransformation,
} from '../../PageConfigProvider/types/item';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import statusStyles from '../chart-status.module.css';
import { LAYER_CONFIGS } from './ChoroplethRenderer/ChoroplethConfig';
import { ChoroplethRenderer } from './ChoroplethRenderer/ChoroplethRenderer';
import { createChoroplethFeatureBuilder } from './ChoroplethRenderer/choroplethFeatureBuilder';
import styles from './GenericMap.module.css';
import { loadMapLibreEngine, type MapEngineModule } from './map-engine';
import {
  applyMaplibreBasemapTheme,
  getMaplibreBasemapStyle,
} from './maplibre-basemap';
import './custom-maplibre.css';

import { useOverviewTooltip } from './hooks/useOverviewTooltip';
import { useSyncViewport } from './hooks/useSyncViewport';
import { useTooltipPortal } from './hooks/useTooltipPortal';
import './popup-css.css';

import { LocationType, type MapPoint, type MapRenderer } from './types';
import { calculateBounds, isCenterZoomBounds } from './utils/calculate-bounds';
import { getMaxZoom, MIN_ZOOM } from './utils/constants';
import {
  getNormalizedGeographySelections,
  hasActiveGeographyFilters,
} from './utils/geography-adapter';
import { getMetricPaletteConfig } from './utils/metric-scales';
import { loadGeneratedSurfaceAreas } from './utils/surface-areas';

interface WebGLDiagnostics {
  contextType: 'webgl2' | 'webgl' | 'experimental-webgl' | 'none';
  vendor: string;
  renderer: string;
  hasDebugRendererInfo: boolean;
  errorMessage?: string;
}

function getWebGLDiagnostics(): WebGLDiagnostics {
  const canvas = document.createElement('canvas');
  const contextTypes = ['webgl2', 'webgl', 'experimental-webgl'] as const;
  let creationErrorMessage = '';

  const onCreationError = (e: Event) => {
    const raw =
      'statusMessage' in e
        ? (e as { statusMessage: unknown }).statusMessage
        : undefined;
    const msg = typeof raw === 'string' ? raw : undefined;
    if (msg && !creationErrorMessage) {
      creationErrorMessage = msg;
    }
  };

  canvas.addEventListener('webglcontextcreationerror', onCreationError);

  for (const contextType of contextTypes) {
    const gl = canvas.getContext(contextType) as WebGLRenderingContext | null;
    if (gl) {
      canvas.removeEventListener('webglcontextcreationerror', onCreationError);
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      const vendor = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : gl.getParameter(gl.VENDOR);

      const renderer = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : gl.getParameter(gl.RENDERER);

      return {
        contextType,
        vendor: vendor ? String(vendor) : 'unknown',
        renderer: renderer ? String(renderer) : 'unknown',
        hasDebugRendererInfo: !!debugInfo,
      };
    }
  }

  canvas.removeEventListener('webglcontextcreationerror', onCreationError);

  return {
    contextType: 'none',
    vendor: 'unknown',
    renderer: 'unknown',
    hasDebugRendererInfo: false,
    errorMessage:
      creationErrorMessage ||
      'WebGL is not supported or hardware acceleration is disabled.',
  };
}

const WORLD_BOUNDS = {
  minLon: -180,
  minLat: -75,
  maxLon: 180,
  maxLat: 85,
};

// MapLibre rejects a max-bounds longitude sitting exactly on the antimeridian,
// so the east/west limits are trimmed by an epsilon.
const LON_EPSILON = 1e-4;
const LON_LIMIT = WORLD_BOUNDS.maxLon - LON_EPSILON;

const MAX_BOUNDS: [[number, number], [number, number]] = [
  [-LON_LIMIT, WORLD_BOUNDS.minLat],
  [LON_LIMIT, WORLD_BOUNDS.maxLat],
];

function clampZoom(zoom: number, minZoom: number, maxZoom: number): number {
  return Math.max(minZoom, Math.min(maxZoom, zoom));
}

export const GenericMap = ({
  points,
  metric,
  syncViewport = true,
  normalization,
  transformation,
  primaryDimension,
  interactive = true,
  isSourceReady,
  'data-testid': testId,
}: {
  points: MapPoint[];
  metric: MetricValues;
  syncViewport?: boolean;
  normalization?: DataNormalization;
  transformation?: DataTransformation;
  primaryDimension?: PrimaryFilterId;
  interactive?: boolean;
  isSourceReady?: (isReady: boolean) => void;
  'data-testid'?: string;
}) => {
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const rendererRef = useRef<MapRenderer | null>(null);

  const isApplyingRemoteViewport = useRef(false);
  const mapIdRef = useRef<string>(
    `generic-map-${Math.random().toString(36).slice(2, 9)}`
  );
  const [isMapReady, setIsMapReady] = useState(false);
  const [webGLError, setWebGLError] = useState<string | null>(null);
  const { kibanaLogger } = useKibanaLogger();

  // From the filter context, NOT a fresh `useGeographyTaxonomy()`: the context
  // resolves the taxonomy WITH the `GEOGRAPHY_TAXONOMY` override a deeplink or
  // saved set carries, and that same resolution builds `geo_tax` on the
  // request — so it decides which granularities the response holds. Resolving
  // the stored preference again here lets the two disagree, and then the map
  // holds metro data with state/county layers (or the reverse), where country
  // is the only level both sets share and nothing below it can paint.
  const { usesStateBreakdownOverMsa } = useFilterContext();
  const { theme: mapTheme } = useChartTheme();
  const mapStyle = getMaplibreBasemapStyle(mapTheme, false);
  const appliedMapThemeRef = useRef(mapTheme);

  const { filters } = useFilters();

  const needsSurfaceAreas =
    (normalization ?? getMetricPaletteConfig(metric).normalization) ===
    'area_density';
  const [surfaceAreasReady, setSurfaceAreasReady] = useState(false);
  useEffect(() => {
    if (!needsSurfaceAreas || surfaceAreasReady) return;
    let cancelled = false;
    let retryTimer: number | undefined;
    let attempts = 0;
    const attempt = () => {
      attempts += 1;
      loadGeneratedSurfaceAreas().then(
        () => {
          if (!cancelled) setSurfaceAreasReady(true);
        },
        (error: unknown) => {
          if (cancelled) return;
          if (attempts < 3) {
            retryTimer = window.setTimeout(attempt, 2000 * attempts);
            return;
          }
          // Failed loads are not cached, so the next mount retries; meanwhile
          // render with the no-data fallback instead of blocking the map.
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          kibanaLogger?.error(
            `Surface-area tables failed to load: ${errorMessage}`,
            {
              context: 'GenericMap.SurfaceAreasLoad',
              url: window.location.href,
            }
          );
          setSurfaceAreasReady(true);
        }
      );
    };
    attempt();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [needsSurfaceAreas, surfaceAreasReady, kibanaLogger]);

  const shouldUseOverviewTooltip =
    (metric === 'headcount' && !!primaryDimension) || metric === 'td_users';

  const boundingBox = useMemo(() => {
    const hasActiveFilters = hasActiveGeographyFilters(filters);
    if (hasActiveFilters) {
      const geographySelections = getNormalizedGeographySelections(
        filters,
        points
      );
      return calculateBounds({ geographySelections });
    } else {
      return calculateBounds({ points });
    }
  }, [filters, points]);

  const enabledConfigs = useMemo(() => {
    const allConfigs = LAYER_CONFIGS;
    return usesStateBreakdownOverMsa
      ? allConfigs.filter(
          (c) => c.id !== LocationType.METRO_AREA && c.id !== LocationType.CITY
        )
      : allConfigs.filter(
          (c) => c.id !== LocationType.STATE && c.id !== LocationType.COUNTY
        );
  }, [usesStateBreakdownOverMsa]);

  const choroplethFeatureBuilder = useMemo(
    () => createChoroplethFeatureBuilder(points, enabledConfigs),
    [points, enabledConfigs]
  );

  const {
    initializeTooltipHandlers,
    handleMapHover,
    tooltipState,
    handleTooltipResize,
  } = useOverviewTooltip(primaryDimension, {
    enabled: shouldUseOverviewTooltip,
    buildFeature: choroplethFeatureBuilder,
    metric,
  });

  const tooltipPortal = useTooltipPortal(
    shouldUseOverviewTooltip ? tooltipState : null,
    handleTooltipResize
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    // The engine arrives via dynamic import, so creation is deferred a tick
    // and must be cancellable on unmount.
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const createMap = (engineLib: MapEngineModule) => {
      if (!mapContainerRef.current || mapRef.current) return;

      let initialBounds: LngLatBoundsLike | null = null;
      if (boundingBox && !isCenterZoomBounds(boundingBox)) {
        initialBounds = boundingBox;
      }

      appliedMapThemeRef.current = mapTheme;
      const mapOptions: MapOptions = {
        container: mapContainerRef.current,
        style: mapStyle,
        minZoom: MIN_ZOOM,
        maxZoom: getMaxZoom(points),
        attributionControl: false,
        renderWorldCopies: false,
        maxBounds: MAX_BOUNDS,
        dragRotate: false,
        touchPitch: false,
        // MapLibre v5 moved the WebGL context attributes here from the top
        // level. Without it the canvas is not readable by `toDataURL()`, so a
        // report export captures a blank map (see pdf-exporter).
        canvasContextAttributes: { preserveDrawingBuffer: isReport },
      };

      if (initialBounds) {
        mapOptions.bounds = initialBounds;
        mapOptions.fitBoundsOptions = {
          padding: 20,
          duration: 0,
          maxZoom: getMaxZoom(points),
          minZoom: MIN_ZOOM,
        };
      } else {
        mapOptions.center = [10, 24];
        mapOptions.zoom = clampZoom(1.2, MIN_ZOOM, getMaxZoom(points));
      }

      try {
        const map = new engineLib.Map(mapOptions);
        mapRef.current = map;

        // Speed up zoom rate of mouse wheel and trackpad
        map.scrollZoom.setWheelZoomRate(1 / 360);
        map.scrollZoom.setZoomRate(1 / 80);

        map.on('load', () => {
          const renderer = new ChoroplethRenderer(
            usesStateBreakdownOverMsa,
            mapTheme
          );
          rendererRef.current = renderer;

          const initOptions = shouldUseOverviewTooltip
            ? {
                initializeTooltipHandlers,
                onHover: handleMapHover,
                interactive,
              }
            : { interactive };

          renderer.init(map, initOptions);

          setIsMapReady(true);
        });

        const resizeObserver = new ResizeObserver(() => {
          setTimeout(() => {
            if (mapRef.current && mapRef.current.getContainer()) {
              mapRef.current.resize();
            }
          }, 0);
        });
        resizeObserver.observe(mapContainerRef.current);

        cleanup = () => {
          resizeObserver.disconnect();

          if (mapRef.current) {
            rendererRef.current?.destroy();
            mapRef.current.remove();
            mapRef.current = null;
            rendererRef.current = null;
          }
          // A re-run of this effect (geography mode) builds a brand new map +
          // renderer holding no data. The data effect below is keyed on
          // isMapReady, so without resetting it here it never re-fires and
          // the fresh choropleth stays fully transparent.
          setIsMapReady(false);
        };
      } catch (error) {
        const diag = getWebGLDiagnostics();
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setWebGLError(errorMessage);
        isSourceReady?.(true);

        const reasonStr = diag.errorMessage
          ? ` | reason: ${diag.errorMessage}`
          : '';
        const diagMessage = `WebGL initialization failed: ${errorMessage} | contextType: ${diag.contextType} | vendor: ${diag.vendor} | renderer: ${diag.renderer} | hasDebugRendererInfo: ${diag.hasDebugRendererInfo}${reasonStr}`;

        kibanaLogger?.error(diagMessage, {
          context: 'GenericMap.WebGLInit',
          url: window.location.href,
        });
      }
    };

    loadMapLibreEngine().then(
      (engineLib) => {
        if (!cancelled) createMap(engineLib);
      },
      (error: unknown) => {
        if (cancelled) return;
        // Without this the chunk failure is an unhandled rejection: no map,
        // no error state, and isSourceReady never resolves — a report export
        // would wait on this tile forever.
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setWebGLError(errorMessage);
        isSourceReady?.(true);
        kibanaLogger?.error(`MapLibre engine failed to load: ${errorMessage}`, {
          context: 'GenericMap.MapLibreLoad',
          url: window.location.href,
        });
      }
    );

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [usesStateBreakdownOverMsa]);

  // Swap the basemap theme in place on theme change — recreating the map
  // would reset the user's viewport. The swap is paint-only, so the sources,
  // layers and handlers all survive.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    if (appliedMapThemeRef.current === mapTheme) return;
    appliedMapThemeRef.current = mapTheme;

    applyMaplibreBasemapTheme(map, mapTheme, false);
    rendererRef.current?.setTheme(mapTheme);
  }, [mapTheme, isMapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    map.setMaxZoom(getMaxZoom(points));
  }, [points, isMapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || isApplyingRemoteViewport.current) return;

    if (!boundingBox) return;

    const maxZoom = getMaxZoom(points);

    if (isCenterZoomBounds(boundingBox)) {
      const clampedZoom = clampZoom(boundingBox.zoom, MIN_ZOOM, maxZoom);
      const clampedCenter: [number, number] = [
        Math.max(
          WORLD_BOUNDS.minLon,
          Math.min(WORLD_BOUNDS.maxLon, boundingBox.center[0])
        ),
        Math.max(
          WORLD_BOUNDS.minLat,
          Math.min(WORLD_BOUNDS.maxLat, boundingBox.center[1])
        ),
      ];
      map.easeTo({
        center: clampedCenter,
        zoom: clampedZoom,
        essential: true,
        duration: 0,
      });
    } else {
      const camera = map.cameraForBounds(boundingBox, {
        padding: 20,
        maxZoom,
      });

      if (!camera) return;

      map.fitBounds(boundingBox, {
        padding: 20,
        essential: true,
        duration: 0,
        maxZoom,
        minZoom: MIN_ZOOM,
      });
    }
  }, [boundingBox, isMapReady, points]);

  useSyncViewport({
    mapRef,
    mapIdRef,
    isEnabled: syncViewport,
    isReady: isMapReady,
    isApplyingRemoteViewportRef: isApplyingRemoteViewport,
  });

  // Update renderer when data/metric/zoom changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!isMapReady || !map || !rendererRef.current) return;
    if (needsSurfaceAreas && !surfaceAreasReady) return;

    const effectiveMapOptions =
      normalization || (transformation && transformation !== 'none')
        ? { normalization, transformation }
        : undefined;

    rendererRef.current.update(points, metric, {
      mapOptions: effectiveMapOptions,
    });
    isSourceReady?.(true);
  }, [
    points,
    metric,
    isMapReady,
    normalization,
    transformation,
    isSourceReady,
    needsSurfaceAreas,
    surfaceAreasReady,
  ]);

  if (webGLError) {
    return (
      <div
        data-testid={testId}
        className={classNames(
          statusStyles.statusPanel,
          statusStyles.statusPanelScrollable
        )}
      >
        <Text weight="bold" className={statusStyles.statusTitle}>
          Unable to Load Map
        </Text>
        <Text fontSize="sm" className={statusStyles.statusDetail}>
          Your browser could not initialize WebGL, which is required to display
          the map.
        </Text>
        <Text fontSize="sm" className={statusStyles.statusDetail}>
          Please check that WebGL (graphics acceleration) is enabled in your
          browser settings.
        </Text>
        <Text fontSize="sm" className={statusStyles.statusDetail}>
          <a
            className={styles.webglLink}
            href="https://get.webgl.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Check WebGL support
          </a>
        </Text>
      </div>
    );
  }

  return (
    <>
      <div data-testid={testId} className={styles.mapFrame}>
        <div
          ref={mapContainerRef}
          className={styles.mapContainer}
          data-testid="maplibre-map-container"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        />
      </div>
      {tooltipPortal}
    </>
  );
};
